import React from 'react';
import { Search } from 'lucide-react';
import './listPage.css';

/**
 * Μία γραμμή πάνω από τον πίνακα: αναζήτηση αριστερά, φίλτρα (FilterSelect, DateRange) δίπλα της, η κύρια
 * ενέργεια (PRIMARY_BTN) δεξιά και από κάτω «N αποτελέσματα». Σε στενό χώρο αναδιπλώνεται: πρώτα η
 * αναζήτηση σε όλο το πλάτος. Η ίδια η αναζήτηση δεν φιλτράρει τίποτα — αν η σελίδα ψάχνει στη μνήμη,
 * χρησιμοποιεί το matchesSearch/searchKey (χωρίς τόνους, πεζά), αν ψάχνει στον server στέλνει την τιμή.
 */

export interface ListToolbarSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Όνομα για αναγνώστες οθόνης (αλλιώς το placeholder). */
  ariaLabel?: string;
  testId?: string;
}

export interface ListToolbarProps {
  search?: ListToolbarSearch;
  filters?: React.ReactNode;
  /** Δεξιά: η κύρια ενέργεια της σελίδας (π.χ. «Νέος πελάτης»), «Ανανέωση», «Εξαγωγή». */
  actions?: React.ReactNode;
  /** Κάτω από τη γραμμή: «124 κλήσεις», «3 από 40». Ανακοινώνεται (role=status) όταν αλλάζει. */
  summary?: React.ReactNode;
  testId?: string;
}

const present = (node: React.ReactNode): boolean => node !== null && node !== undefined && node !== false && node !== '';

export const ListToolbar: React.FC<ListToolbarProps> = ({ search, filters, actions, summary, testId = 'list-toolbar' }) => {
  const searchName = search ? search.ariaLabel || search.placeholder : undefined;
  return (
    <div className="lp-toolbar" data-testid={testId}>
      {(search || present(filters)) && (
        <div className="lp-tb-main" role={search ? 'search' : undefined} aria-label={searchName}>
          {search && (
            <label className="lp-search">
              <Search className="lp-search-ico" aria-hidden="true" />
              <input
                type="search"
                className="lp-search-input"
                value={search.value}
                placeholder={search.placeholder}
                aria-label={searchName}
                autoComplete="off"
                spellCheck={false}
                enterKeyHint="search"
                data-testid={search.testId ?? 'list-search'}
                onChange={e => search.onChange(e.target.value)}
                // Τα αποτελέσματα ήδη φαίνονται όσο γράφεις· το Enter κλείνει μόνο το πληκτρολόγιο της οθόνης αφής.
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
            </label>
          )}
          {present(filters) && <div className="lp-tb-filters">{filters}</div>}
        </div>
      )}
      {present(actions) && <div className="lp-tb-actions">{actions}</div>}
      {present(summary) && <p className="lp-tb-summary" role="status">{summary}</p>}
    </div>
  );
};

export default ListToolbar;
